import { Component, ElementRef, EventEmitter, forwardRef, Input, Output, ViewChild } from '@angular/core'
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms'
import { getFileExtension } from '@ftr/contracts/type/core'
import { ValidationErrorHintDirective } from '@ftr/forms'
import { AnchorComponent, ButtonSize, FileSizePipe } from '@ftr/foundation'

export type FileChangeHandler = (file: File) => void

export enum FileInputDisplayType {
  /**
   * Displays a file selector button, along with the name and size of the selected file.
   */
  Full = 'full',
  /**
   * Displays a file selector button by itself.
   */
  OnlyButton = 'only-button',
}

export interface SelectedFile {
  name: string
  size: number
}

/**
 * A wrapper for a file input element, including rendering of elements essential to validation display.
 */
@Component({
  selector: 'ftr-file-input',
  templateUrl: './file-input.component.html',
  styleUrls: ['./file-input.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FileInputComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [AnchorComponent, FileSizePipe, ValidationErrorHintDirective],
})
export class FileInputComponent implements ControlValueAccessor, Validator {
  @ViewChild('fileInput') fileInput: ElementRef

  /**
   * How to visually present the file input.
   * @type {FileInputDisplayType}
   */
  @Input() displayType: FileInputDisplayType = FileInputDisplayType.Full
  @Input() label = 'Choose File'
  @Input() validationLabel: string
  @Input() required = false
  @Input() submitAttempted: boolean
  @Input() ngFormControl: AbstractControl
  @Output() fileSelected = new EventEmitter<SelectedFile>()

  /**
   * File extensions which the user is allowed to upload. If the user selects a file that doesn't end in one of these
   * extensions, a validation error will be shown.
   */
  @Input() acceptableFileExtensions: string[] = []

  file: File | undefined
  selectedFile: SelectedFile
  controlTouched = false

  readonly buttonSize = ButtonSize

  private changeHandler?: FileChangeHandler

  writeValue(obj?: File): void {
    this.file = obj
  }

  registerOnChange(fn: FileChangeHandler): void {
    this.changeHandler = fn
  }

  registerOnTouched(_: Function): void {
    // No-op: if (touch) event support is required, this will need to be implemented
  }

  onChange(event: Event): void {
    const input = event.target as HTMLInputElement
    this.controlTouched = true
    this.file = input && input.files ? input.files[0] : undefined
    this.selectedFile = {
      name: this.file ? this.file.name : '',
      size: this.file ? this.file.size : 0,
    }

    this.fileSelected.emit(this.selectedFile)
    this.notifyChanges()
  }

  validate(): ValidationErrors | null {
    if (!this.selectedFile || !this.selectedFile.name) {
      return null
    }

    const extension = getFileExtension(this.selectedFile.name)
    const emptyFile = this.file!.size === 0
    const extensionInvalid = !extension || !this.acceptableFileExtensions.includes(extension)

    if (!extensionInvalid && !emptyFile) {
      return null
    }

    return {
      invalidFileExtension: extensionInvalid ? `Files with extension ".${extension}" are not allowed.` : undefined,
      emptyFile: emptyFile ? 'Files with no contents are not allowed.' : undefined,
    }
  }

  private notifyChanges(): void {
    if (this.changeHandler) {
      this.changeHandler(this.file!)
    }
  }
}
